import {
  type Configuration,
  LogLevel,
  type PopupRequest,
  PublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser';

import { appConfig } from '@/config/appConfig';

/**
 * MSAL configuration for the SPA.
 *
 * - `authority` is constructed from the tenant id, supporting GUIDs as well as
 *   the multi-tenant aliases (`common`, `organizations`, `consumers`).
 * - Tokens are stored in `sessionStorage` so they don't persist across windows
 *   and don't leak between user sessions on shared machines.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: appConfig.entraClientId,
    authority: `https://login.microsoftonline.com/${appConfig.entraTenantId}`,
    redirectUri: appConfig.entraRedirectUri,
    postLogoutRedirectUri: appConfig.entraPostLogoutRedirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error(`[msal] ${message}`);
        else if (level === LogLevel.Warning) console.warn(`[msal] ${message}`);
      },
    },
  },
};

/** Login request used both for redirect and silent token acquisition.
 *
 * The Kai-API scope (when configured) is requested AT LOGIN so the user
 * consents once on the sign-in screen. Without it, the first API call has to
 * add the scope via a silent hidden-iframe request, which cannot render a
 * consent prompt and fails with monitor_window_timeout. */
export const loginRequest: RedirectRequest & PopupRequest = {
  scopes: [...appConfig.entraScopes, ...(appConfig.apiScope ? [appConfig.apiScope] : [])],
};

/**
 * Module-level MSAL instance shared by AuthProvider (UI) and the API client
 * (imperative). One instance keeps the active account consistent between the
 * two consumers.
 */
export const msalInstance = new PublicClientApplication(msalConfig);
